import { Config } from "@app/config";
import type { FormIncomeSimpleData } from "@shared/ui";
import { Console } from "@shared/lib/console";
import { getKOs } from "./BabyLoanSmallCalculator.fn";
import { INCOME_ACCEPTABLE_RATE } from "./data";
import type { BabyLoanRequest, BabyLoanResponse, EligibleForBabyLoan, IncomeData } from "./types";
import { ErrorCode } from "./types";

const MAX_LOAN_AMOUNT = 11_000_000;

function getIncomeData(incomeList: FormIncomeSimpleData[]): IncomeData {
	let totalIncome = 0;
	let singleIncome = 0;
	let hasAcceptableIncome = false;

	incomeList.forEach((income) => {
		const type = income.incomeType;
		const { rate, isSingleAcceptable } = INCOME_ACCEPTABLE_RATE[type];
		let weightedAmount = income.receiptAmount * rate;

		if (isNaN(weightedAmount)) {
			Console.error(`Missing income type: ${type}`);
			weightedAmount = 0;
		}

		if (isSingleAcceptable) {
			singleIncome += weightedAmount;

			if (weightedAmount > Config.getMinWage()) {
				hasAcceptableIncome = true;
			}
		}
		totalIncome += weightedAmount;
	});

	if (!hasAcceptableIncome) {
		totalIncome = 0;
		singleIncome = 0;
	}

	return { totalIncome, hasAcceptableIncome, singleIncome };
}

function getMonthlyInstallment(loanAmount: number): number {
	return (loanAmount / MAX_LOAN_AMOUNT) * 90_000;
}

function getFreelyUsableMonthlyInstallment(loanAmount: number, maxFreelyUsableLoanAmount: number): number {
	return Math.floor((Math.min(loanAmount, maxFreelyUsableLoanAmount) / MAX_LOAN_AMOUNT) * 90_000);
}

function getRealInstallment(loanAmount: number, maxLoanAmount: number): number {
	return Math.floor((Math.min(loanAmount, maxLoanAmount) * 1.1) / 240);
}

function getMaxInstallment(totalIncome: number): number {
	let maxInstallment = totalIncome;

	if (totalIncome >= 500_000) {
		maxInstallment *= 0.6;
	} else if (totalIncome >= 150_000) {
		maxInstallment *= 0.5;
	} else {
		maxInstallment *= 0.4;
	}

	return maxInstallment;
}

function getMaxFreelyUsableInstallment(
	totalIncome: number,
	currentLoansMonthlyInstallment: number,
	creditLimitSumValue: number,
): number {
	let maxFreelyUsableInstallment = totalIncome;

	if (totalIncome >= 500_000) {
		maxFreelyUsableInstallment = Math.max(
			0,
			maxFreelyUsableInstallment * 0.6 - currentLoansMonthlyInstallment - creditLimitSumValue * 0.05,
		);
	} else if (totalIncome >= 150_000) {
		maxFreelyUsableInstallment = Math.max(
			0,
			maxFreelyUsableInstallment * 0.5 - currentLoansMonthlyInstallment - creditLimitSumValue * 0.05,
		);
	} else {
		maxFreelyUsableInstallment = Math.max(
			0,
			maxFreelyUsableInstallment * 0.4 - currentLoansMonthlyInstallment - creditLimitSumValue * 0.05,
		);
	}

	return maxFreelyUsableInstallment;
}

export function getErrorMessage(error: ErrorCode): string {
	switch (error) {
		case ErrorCode.AGE:
			return "A feleségnek 18 évnél idősebbnek és 41 évnél fiatalabbnak kell lennie.";
		case ErrorCode.MARRIED:
			return "Csak házasok igényelhetik a támogatást.";
		case ErrorCode.ADDRESS:
			return "Mindkét házasfélnek magyar lakcímmel kell rendelkeznie.";
		case ErrorCode.NAV:
			return "Egyik félnek sem lehet az állami adóhatóságnál köztartozása (pl. adótartozás).";
		case ErrorCode.KHR:
			return "Egyik fél sem szerepelhet elmaradással a KHR-ben.";
		case ErrorCode.SOC:
			return "Legalább az egyik félnek minimum 3 éves TB jogosultsággal kell rendelkeznie.";
		case ErrorCode.DAYSSOCINHUN:
			return "Az igénylést megelőző utolsó 180 napnak magyar TB jogviszonyból kell származnia.";
		default:
			return "A megadott jövedelem alapján nem adható Babaváró hitel";
	}
}

export function calculate(values: BabyLoanRequest): BabyLoanResponse {
	const { creditLimitSumValue, currentLoansMonthlyInstallment, loanAmount, incomeList } = values;

	// Jövedelem adatok
	const { totalIncome, hasAcceptableIncome, singleIncome } = getIncomeData(incomeList);

	// Hitel adatok
	const monthlyInstallment = getMonthlyInstallment(loanAmount);
	const maxInstallment = getMaxInstallment(totalIncome);
	const maxFreelyUsableInstallment = getMaxFreelyUsableInstallment(
		totalIncome,
		currentLoansMonthlyInstallment,
		creditLimitSumValue,
	);

	let maxLoanAmount = Math.min(MAX_LOAN_AMOUNT, (maxInstallment * MAX_LOAN_AMOUNT) / 90_000);
	let maxFreelyUsableLoanAmount = Math.min(MAX_LOAN_AMOUNT, (maxFreelyUsableInstallment * MAX_LOAN_AMOUNT) / 90_000);

	maxLoanAmount = Math.floor(maxLoanAmount / 100_000) * 100_000;
	maxFreelyUsableLoanAmount = Math.floor(maxFreelyUsableLoanAmount / 100_000) * 100_000;

	const freelyUsableMonthlyInstallment = getFreelyUsableMonthlyInstallment(loanAmount, maxFreelyUsableLoanAmount);
	const realInstallment = getRealInstallment(loanAmount, maxLoanAmount);
	const freelyUsableRealInstallment = getRealInstallment(loanAmount, maxFreelyUsableLoanAmount);
	const realLoanAmount = Math.min(loanAmount, maxLoanAmount);
	const freelyUsableRealLoanAmount = Math.min(loanAmount, maxFreelyUsableLoanAmount);

	// Megnézzük a KO-kat
	const errors = getKOs(values);

	if (!hasAcceptableIncome || freelyUsableRealLoanAmount === 0) {
		errors.push(ErrorCode.NO_INCOME);
	}

	// Melyik végkimenetlbe esik
	let eligibleForBabyLoan: EligibleForBabyLoan = "";
	let eligibleForBabyLoanValue = 0;

	if (!hasAcceptableIncome || values.inKHR) {
		eligibleForBabyLoan = "NO_NOT_CREDITABLE";
		eligibleForBabyLoanValue = 5;
	} else if (errors.length === 0) {
		if (monthlyInstallment <= maxInstallment) {
			eligibleForBabyLoan = "YES_FULL_AMOUNT";
			eligibleForBabyLoanValue = 1;
		} else {
			eligibleForBabyLoan = "YES_BUT_LESS_THAN_FULL";
			eligibleForBabyLoanValue = 2;
		}
	} else if (monthlyInstallment <= maxInstallment) {
		eligibleForBabyLoan = "NO_OTHER_PRODUCT_RECOMMENDED";
		eligibleForBabyLoanValue = 3;
	} else {
		eligibleForBabyLoan = "NO_OTHER_PRODUCT_RECOMMENDED_WITH_LESS_AMOUNT";
		eligibleForBabyLoanValue = 4;
	}

	if (errors.length) {
		return {
			totalIncome,
			singleIncome,
			hasAcceptableIncome,
			eligibleForBabyLoan,
			monthlyInstallment: 0,
			maxInstallment: 0,
			maxLoanAmount: 0,
			freelyUsableMonthlyInstallment: 0,
			maxFreelyUsableInstallment: 0,
			maxFreelyUsableLoanAmount: 0,
			realInstallment: 0,
			freelyUsableRealInstallment: 0,
			realLoanAmount: 0,
			freelyUsableRealLoanAmount: 0,
			eligibleForBabyLoanValue,
			errors,
		};
	}

	// Adatok hozzáfűzése a leadhez
	return {
		totalIncome,
		singleIncome,
		hasAcceptableIncome,
		eligibleForBabyLoan,
		monthlyInstallment,
		maxInstallment,
		maxLoanAmount,
		freelyUsableMonthlyInstallment,
		maxFreelyUsableInstallment,
		maxFreelyUsableLoanAmount,
		realInstallment,
		freelyUsableRealInstallment,
		realLoanAmount,
		freelyUsableRealLoanAmount,
		eligibleForBabyLoanValue,
		errors,
	};
}
